/**
 * Request-level cache and in-flight de-duplication for `/api/...` GETs.
 *
 * Every chart on the district page fetches through here. Three things this buys, all of which the
 * per-component `useState` + `useEffect` fetches could not:
 *
 * 1. **De-duplication.** A tab renders many cards, and several legitimately want the same series
 *    (e.g. `voterfile/counts?demographic_category=topline&...` is both the header's registered-voter
 *    count and a chart). Concurrent callers for one URL share a single request.
 * 2. **Survival across unmount.** Tab content is unmounted when the tab changes, so returning to a
 *    tab previously re-fetched everything from scratch. The cache outlives the component, so the
 *    return is served synchronously with no request and no loading flash.
 * 3. **One parse.** The backend sometimes hands back a JSON *string* rather than an object; the
 *    parse happens once, at insert, rather than on every consumer.
 *
 * Deliberately not cached: failures. A failed request stays failed only for the callers awaiting it,
 * so a retry (remount, tab return) genuinely retries.
 */

import apiClient from "./axios";

/** How long a cached body stays servable. Voterfile aggregates change on an ETL cadence, not live. */
// Voterfile aggregates change on an ETL cadence, not live -- atlas is refreshed MONTHLY. Five
// minutes meant 288 expiries a day per browser tab against data that moves twelve times a year:
// on the order of 8,640 discards per actual change, each one a full round trip to a 1-vCPU API.
//
// One hour, raised from five minutes on 2026-08-28. Still a clock rather than a data version,
// because nothing publishes one for atlas yet: `data_versions` carries only `boundaries` (stamped
// by data_pipeline/boundaries.py), which is why the map layer cache CAN key on a version and this
// one cannot. Having atlas ingest stamp `data_versions.atlas` is the change that would let this
// expire exactly when the data moves, and nothing sooner.
export const DEFAULT_TTL_MS = 60 * 60 * 1000;

/**
 * Bound on retained bodies. A district page with a comparison touches on the order of 60 distinct
 * URLs; this holds several districts' worth before evicting the oldest.
 */
const MAX_ENTRIES = 300;

/**
 * The generation of data every cached body belongs to, from the API's `X-Data-Version` response
 * header. Null until a response carries one.
 *
 * THIS IS WHAT THE TTL ABOVE WAS STANDING IN FOR. A clock cannot tell a body that is still correct
 * from one that is not, so it discards both. This can: the API stamps every response with a version
 * derived from the content checksums of the loaded voter file, so a response carrying a version we
 * have not seen means the data actually moved -- and nothing else does.
 *
 * The TTL stays as a backstop for a deployment that publishes no version at all (the header is
 * absent, or CORS is not exposing it), where this degrades to exactly the previous behaviour.
 */
let currentDataVersion = null;

/** url -> { value, expiresAt, version } */
const entries = new Map();
/** url -> Promise, for requests that have not settled yet */
const inFlight = new Map();
/**
 * batch URL -> time (ms) before which it is not re-sent, after it (or its fallbacks) failed. Without
 * this, a failed batch would be re-sent on every re-render of the component that registers it
 * (fetchBatch is called during render, and failures are deliberately not cached) -- a retry storm
 * the per-card fetches never had, since those only fetch on mount or URL change.
 */
const batchFailures = new Map();
/** How long a failed batch URL is held back from re-sending. */
export const BATCH_FAILURE_COOLDOWN_MS = 30 * 1000;

function now() {
  return Date.now();
}

/** FastAPI sometimes returns a JSON-encoded string rather than a decoded body. */
function parseBody(body) {
  return typeof body === "string" ? JSON.parse(body) : body;
}

function evictIfNeeded() {
  while (entries.size > MAX_ENTRIES) {
    // Map preserves insertion order, so the first key is the oldest write.
    const oldest = entries.keys().next();
    if (oldest.done) {
      return;
    }
    entries.delete(oldest.value);
  }
}

/**
 * Cached body for `url`, or `undefined` when there is nothing fresh.
 *
 * Synchronous by design: a component can hydrate during its first render and skip the loading state
 * entirely. `undefined` means "not cached" — a cached body is whatever the API returned.
 *
 * Read-only, including for expired entries: components call this *during render*, so it must not
 * mutate module state. Expired entries are left for MAX_ENTRIES eviction to reclaim.
 */
export function getCachedData(url) {
  if (!url) {
    return undefined;
  }
  const entry = entries.get(url);
  if (!entry || entry.expiresAt <= now()) {
    return undefined;
  }
  // A body written under an older generation of the data is stale however recently it was fetched.
  if (entry.version != null && currentDataVersion != null && entry.version !== currentDataVersion) {
    return undefined;
  }
  return entry.value;
}

/**
 * Note the data version a response reported, dropping everything older when it changed.
 *
 * Exported for tests and for callers holding a raw response. Returns true when the version moved,
 * which is the only condition under which anything is discarded.
 */
export function noteDataVersion(version) {
  if (!version || version === currentDataVersion) {
    return false;
  }
  const hadPrevious = currentDataVersion != null;
  currentDataVersion = version;
  if (hadPrevious) {
    // The data moved. Everything held predates it.
    entries.clear();
    return true;
  }
  return false;
}

/** The generation of data this tab currently believes it holds. */
export function getDataVersion() {
  return currentDataVersion;
}

/** Seed the cache without a request (used by callers that already hold a response). */
export function primeData(url, value, { ttlMs = DEFAULT_TTL_MS } = {}) {
  if (!url) {
    return;
  }
  entries.set(url, { value, expiresAt: now() + ttlMs, version: currentDataVersion });
  evictIfNeeded();
}

/**
 * GET `url`, serving from cache and sharing one request between concurrent callers.
 *
 * @param {string} url absolute API path, e.g. `/api/voterfile/counts?state=NC`
 * @param {{ ttlMs?: number, force?: boolean }} [options] `force` bypasses the cached body but still
 *   joins an in-flight request for the same URL.
 * @returns {Promise<unknown>} the parsed response body
 */
export function fetchData(url, { ttlMs = DEFAULT_TTL_MS, force = false } = {}) {
  if (!force) {
    const cached = getCachedData(url);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
  }

  const pending = inFlight.get(url);
  if (pending) {
    return pending;
  }

  const request = apiClient
    .get(url)
    .then((response) => {
      // Before storing: if this response says the data moved, drop what we held. Ordering matters
      // -- clearing after the write would discard the body we just fetched.
      noteDataVersion(response?.headers?.["x-data-version"]);
      const value = parseBody(response.data);
      primeData(url, value, { ttlMs });
      return value;
    })
    .finally(() => {
      // Only clear our own entry: a `force` refetch may have replaced it.
      if (inFlight.get(url) === request) {
        inFlight.delete(url);
      }
    });

  inFlight.set(url, request);
  return request;
}

/**
 * Fill several per-URL cache entries from ONE request (scale step 4: `/api/voterfile/counts/batch`).
 *
 * Each child is `{ url, key }`: `url` is the exact per-item URL consumers already fetch (and cache
 * under), `key` is where its body sits in the batch response. Children already cached or already in
 * flight are skipped, so calling this repeatedly -- including during render, and under StrictMode's
 * double render -- is safe and sends at most one request per missing set.
 *
 * Every missing child is registered in `inFlight` BEFORE the request resolves, so a consumer that
 * calls `fetchData(childUrl)` meanwhile joins the batch instead of sending its own request.
 *
 * If the batch fails -- including a backend that doesn't have the batch route yet (frontend deployed
 * first) -- each child falls back to its own plain GET, so the page degrades to exactly the old
 * behaviour rather than breaking. A child missing from an otherwise successful response falls back
 * the same way.
 *
 * @param {{ url: string, key: string }[]} children
 * @param {(keys: string[]) => string} buildBatchUrl absolute URL for the keys still needed
 * @param {{ ttlMs?: number, minChildren?: number }} [options] `minChildren`: send nothing unless at
 *   least this many children are still missing
 * @returns {Promise<void>} settles when every child has settled; never rejects
 */
export function fetchBatch(
  children,
  buildBatchUrl,
  { ttlMs = DEFAULT_TTL_MS, minChildren = 1 } = {}
) {
  const missing = children.filter(
    ({ url }) => url && getCachedData(url) === undefined && !inFlight.has(url)
  );
  // Below `minChildren` a batch buys nothing over the consumers' own requests; leave it to them.
  if (missing.length === 0 || missing.length < minChildren) {
    return Promise.resolve();
  }

  const batchUrl = buildBatchUrl(missing.map(({ key }) => key));
  const heldUntil = batchFailures.get(batchUrl);
  if (heldUntil !== undefined && heldUntil > now()) {
    return Promise.resolve();
  }

  // Wrapped so that nothing the client does -- a synchronous throw, a non-promise return -- can
  // escape: callers invoke this during render, where an exception would take down the section.
  // Any such failure becomes an ordinary rejection, and every child falls back to its own GET.
  const get = (url) =>
    Promise.resolve()
      .then(() => apiClient.get(url))
      .then((response) => {
        noteDataVersion(response?.headers?.["x-data-version"]);
        return parseBody(response?.data);
      });

  const batch = get(batchUrl);

  const settled = missing.map(({ url, key }) => {
    const single = () => get(url);
    const child = batch
      .then((body) => (body && body[key] !== undefined ? body[key] : single()), single)
      .then((value) => {
        primeData(url, value, { ttlMs });
        return value;
      })
      .finally(() => {
        if (inFlight.get(url) === child) {
          inFlight.delete(url);
        }
      });
    inFlight.set(url, child);
    // Consumers that joined see the rejection; this copy only keeps it from surfacing as unhandled.
    return child.then(
      () => true,
      () => false
    );
  });

  return Promise.all(settled).then((results) => {
    if (results.every(Boolean)) {
      batchFailures.delete(batchUrl);
    } else {
      batchFailures.set(batchUrl, now() + BATCH_FAILURE_COOLDOWN_MS);
    }
  });
}

/**
 * Drop cached bodies. No argument clears everything; a string drops every URL containing it, which
 * is how a mutation invalidates one district (`invalidateData("state=NC")`).
 *
 * In-flight requests are left alone — they will simply repopulate.
 */
export function invalidateData(match) {
  if (match === undefined) {
    entries.clear();
    return;
  }
  for (const url of Array.from(entries.keys())) {
    if (url.includes(match)) {
      entries.delete(url);
    }
  }
}

/**
 * Drop every cached body *and* every in-flight request, returning the module to its initial state.
 *
 * For tests only: the cache is module-level, so without this a suite's second test that requests an
 * already-cached URL never reaches its `apiClient.get` mock. Wired into src/setupTests.js.
 */
export function resetDataCache() {
  entries.clear();
  inFlight.clear();
  batchFailures.clear();
  // Module state, and therefore leaks between tests exactly like the maps above do.
  currentDataVersion = null;
}

/** Test/diagnostic view of cache occupancy. */
export function cacheStats() {
  return { entries: entries.size, inFlight: inFlight.size };
}
